import React, { useState, useEffect } from 'react';
import { Box, TextField, Typography, Paper } from '@mui/material';
import SEO, { createSoftwareAppSchema } from '../../components/SEO';
import ToolPageContainer from '../../components/ToolPageContainer';

const InfoRow = ({ title, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
    <Typography variant="body1">{title}:</Typography>
    <Paper variant="outlined" sx={{ p: 1, bgcolor: 'action.hover' }}>
      <Typography fontFamily="monospace">{value}</Typography>
    </Paper>
  </Box>
);

function MongoIdConverter() {
  const [mongoId, setMongoId] = useState('507f191e810c19729de860ea');
  const [extracted, setExtracted] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (mongoId.trim() === '') {
      setError('');
      setExtracted(null);
      return;
    }
    if (!/^[a-f\d]{24}$/i.test(mongoId)) {
      setError('Invalid ObjectID format (must be 24 hex characters).');
      setExtracted(null);
      return;
    }

    setError('');
    try {
      const timestampHex = mongoId.substring(0, 8);
      const timestamp = parseInt(timestampHex, 16) * 1000;
      const date = new Date(timestamp);

      const machineId = mongoId.substring(8, 14);
      const processId = mongoId.substring(14, 18);
      const counter = mongoId.substring(18, 24);

      setExtracted({
        dateUTC: date.toUTCString(),
        dateLocal: date.toLocaleString(),
        machineId,
        processId,
        counter: parseInt(counter, 16),
      });
    } catch (e) {
      setError('Failed to parse ObjectID.');
      setExtracted(null);
    }
  }, [mongoId]);

  return (
    <>
      <SEO
        title="MongoDB ObjectID Inspector | Extract Timestamp | UmbrellaCo"
        description="Decode MongoDB ObjectIDs to extract the creation timestamp, machine ID, process ID, and counter. Free online database utility."
        keywords="mongodb objectid, extract timestamp from objectid, decode mongodb id, mongodb objectid parser, database tools"
        url="/dev-tools/mongo-id"
        jsonLd={createSoftwareAppSchema(
          'MongoDB ObjectID Inspector',
          'Decode MongoDB ObjectIDs to extract timestamps and internal identifiers.',
          'https://umbrellaco.in/dev-tools/mongo-id',
          ['Database Utilities', 'MongoDB Tools', 'Data Decoding']
        )}
      />
      <ToolPageContainer
        title="MongoDB ObjectID Inspector"
        description="Paste a MongoDB ObjectID to parse and extract its embedded creation timestamp, machine identifier, and process details."
      >
      <TextField
        label="MongoDB ObjectID"
        value={mongoId}
        onChange={(e) => setMongoId(e.target.value)}
        error={!!error}
        helperText={error || ' '}
        fullWidth
        placeholder="e.g., 507f191e810c19729de860ea"
      />
      
      {extracted && (
        <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
          <InfoRow title="Timestamp (UTC)" value={extracted.dateUTC} />
          <InfoRow title="Timestamp (Local)" value={extracted.dateLocal} />
          <InfoRow title="Machine ID" value={extracted.machineId} />
          <InfoRow title="Process ID" value={extracted.processId} />
          <InfoRow title="Counter" value={extracted.counter} />
        </Paper>
      )}
      </ToolPageContainer>
    </>
  );
}

export default MongoIdConverter;
